import { useEffect, useRef } from "react";

import { Toast } from "@newsela/angelou";

import globalToasterRef from "@/base/globalToasterRef";
import { User } from "@/types/models/User.interface";
import useResetPasswordPost from "@/users/useResetPasswordPost";
import useSignOut from "@/users/v3/useSignOut";
import useUser from "@/users/useUser";
import { ResetPasswordPayload } from "./MyOwnPasswordReset/MyOwnPasswordReset";
import MyOwnPasswordReset from "./MyOwnPasswordReset";
import HardPasswordReset from "./HardPasswordReset";

interface ResetAccountPasswordProps {
  user: Pick<User, "id" | "_editable">;
  canEdit?: boolean;
  minPasswordLength?: number;
}

export type OnResetPasswordReturn = Promise<{ message: string } | null>;

const MIN_PASSWORD_LENGTH = 6;

const ResetAccountPassword = ({
  user,
  canEdit = true,
  minPasswordLength = MIN_PASSWORD_LENGTH,
}: ResetAccountPasswordProps): JSX.Element => {
  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { data: loggedInUser } = useUser();
  const { signOut } = useSignOut();
  const [resetPasswordPost] = useResetPasswordPost();

  useEffect(() => () => clearTimeout(timeoutId.current ?? undefined), []);

  const { id } = user;
  const isEditingMyself = loggedInUser.id === id;
  const props = {
    canEdit: canEdit && user?._editable.password,
    minPasswordLength,
  };

  const onClickResetPassword = async (
    payload: ResetPasswordPayload | Pick<ResetPasswordPayload, "newPassword">,
  ): OnResetPasswordReturn => {
    const resetPasswordResponse = await resetPasswordPost({ id, ...payload });

    if (resetPasswordResponse.error) {
      try {
        const { error } = JSON.parse(
          resetPasswordResponse.error.toJSON().message,
        );

        globalToasterRef.current.displayToast({
          showIcon: true,
          statusColor: Toast.statusColor.danger,
          children: error,
        });
      } catch {
        globalToasterRef.current.displayToast({
          showIcon: true,
          statusColor: Toast.statusColor.danger,
          children: "Update failed",
        });
      }

      return null;
    }

    if (resetPasswordResponse.data && isEditingMyself) {
      globalToasterRef.current.displayToast({
        showIcon: true,
        statusColor: Toast.statusColor.success,
        children: "Password reset successful. Redirecting to sign in...",
      });

      timeoutId.current = setTimeout(() => {
        signOut(false).catch(() => {
          globalToasterRef.current.displayToast({
            showIcon: true,
            statusColor: Toast.statusColor.danger,
            children: "Sign out failed. Please try again.",
          });
        });
      }, 3_000);

      return null;
    }

    globalToasterRef.current.displayToast({
      showIcon: true,
      statusColor: Toast.statusColor.success,
      children: "Password reset successful",
    });

    return resetPasswordResponse.data;
  };

  if (isEditingMyself) {
    return (
      <MyOwnPasswordReset
        {...props}
        onResetPasswordSubmit={onClickResetPassword}
      />
    );
  }

  return (
    <HardPasswordReset
      {...props}
      onResetPasswordSubmit={onClickResetPassword}
    />
  );
};

export default ResetAccountPassword;
